// src/pages/Orders/Orders.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import MainLayout from '../../components/Layout/MainLayout';
import {
  Search,
  Filter,
  Eye,
  Download,
  X,
  Package,
  Truck,
  CheckCircle,
  Clock,
  RotateCcw,
  ArrowUpDown,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import './Orders.css';
import Cookies from 'js-cookie';
import { handleGetApi, handlePostApi } from '../../api/api';

const Orders = () => {
  const token = Cookies.get('auth_token') ? JSON.parse(Cookies.get('auth_token')) : '';
  const headers = {
    Authorization: token
  };

  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);

  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Filters State
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // =========================================================
  // FETCH ORDERS
  // =========================================================
  const fetchOrders = async () => {
    try {
      setLoading(true);

      const res = await handleGetApi('admin/get/orders', headers);
      if (!res.success) {
        throw new Error(res.message || 'Failed to fetch orders');
      }
      setAllOrders(res.data || []);
      setOrders(res.data || []);
    } catch (error) {
      console.error('Fetch Orders Error:', error);
      alert(error.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // =========================================================
  // SEARCH + MULTI-FILTER + SORT
  // =========================================================
  const filteredOrders = useMemo(() => {
    let filtered = [...allOrders];

    // 1. Search Query
    if (search.trim()) {
      const q = search.toLowerCase().trim();

      filtered = filtered.filter((order) => {
        const orderNumber = (order.order_number || '').toLowerCase();
        const customerName = (order.customer_name || '').toLowerCase();
        const firstName = (order.first_name || '').toLowerCase();
        const lastName = (order.last_name || '').toLowerCase();
        const email = (order.email || '').toLowerCase();
        const phone = (order.phone || '').toLowerCase();
        const city = (order.city || '').toLowerCase();
        const razorpayOrder = (order.razorpay_order_id || '').toLowerCase();
        const razorpayPayment = (order.razorpay_payment_id || '').toLowerCase();
        const coupon = (order.coupon_code || '').toLowerCase();

        return (
          orderNumber.includes(q) ||
          customerName.includes(q) ||
          firstName.includes(q) ||
          lastName.includes(q) ||
          email.includes(q) ||
          phone.includes(q) ||
          city.includes(q) ||
          razorpayOrder.includes(q) ||
          razorpayPayment.includes(q) ||
          coupon.includes(q)
        );
      });
    }

    // 2. Order Status Filter
    if (statusFilter) {
      filtered = filtered.filter((order) => {
        const s = (order.order_status || 'pending').toLowerCase().trim();
        return s === statusFilter.toLowerCase().trim();
      });
    }

    // 3. Payment Status Filter
    if (paymentFilter) {
      filtered = filtered.filter((order) => {
        const p = (order.payment_status || 'pending').toLowerCase().trim();
        return p === paymentFilter.toLowerCase().trim();
      });
    }

    // 4. Sorting
    filtered.sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      } else if (sortBy === 'oldest') {
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      } else if (sortBy === 'amount_high') {
        return Number(b.total_amount || 0) - Number(a.total_amount || 0);
      } else if (sortBy === 'amount_low') {
        return Number(a.total_amount || 0) - Number(b.total_amount || 0);
      }
      return 0;
    });

    return filtered;
  }, [allOrders, search, statusFilter, paymentFilter, sortBy]);

  useEffect(() => {
    setOrders(filteredOrders);
  }, [filteredOrders]);

  const hasActiveFilters = Boolean(
    search.trim() || statusFilter || paymentFilter || sortBy !== 'newest'
  );

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPaymentFilter('');
    setSortBy('newest');
  };

  // =========================================================
  // OPEN DETAILS
  // =========================================================
  const handleOpenDetails = async (id) => {
    try {
      const res = await handleGetApi(`admin/get/order/${id}`, headers);
      console.log(res);
      if (res.success) {
        setSelectedOrder(res.data);
        setIsDetailModalOpen(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // =========================================================
  // UPDATE ORDER STATUS
  // =========================================================
  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      setStatusUpdating(true);
      const sendData = {
        order_status: newStatus,
      };
      const res = await handlePostApi(`admin/update/order-status/${orderId}`, sendData, headers);
      console.log(res);
      if (!res.success) {
        throw new Error(res.message || 'Failed to update status');
      }

      // Update list
      const updatedOrders = allOrders.map((order) =>
        order.id === orderId
          ? { ...order, order_status: newStatus }
          : order
      );

      setAllOrders(updatedOrders);

      // Update selected order
      if (selectedOrder) {
        if (selectedOrder.order && selectedOrder.order.id === orderId) {
          setSelectedOrder({
            ...selectedOrder,
            order: {
              ...selectedOrder.order,
              order_status: newStatus,
            },
          });
        } else if (selectedOrder.id === orderId) {
          setSelectedOrder({
            ...selectedOrder,
            order_status: newStatus,
          });
        }
      }
    } catch (error) {
      console.error('Update Status Error:', error);
      alert(error.message);
    } finally {
      setStatusUpdating(false);
    }
  };

  // =========================================================
  // STATUS ICON
  // =========================================================
  const getStatusIcon = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'delivered':
        return <CheckCircle size={14} />;
      case 'shipped':
        return <Truck size={14} />;
      case 'processing':
        return <Clock size={14} />;
      default:
        return <Package size={14} />;
    }
  };

  // =========================================================
  // FORMATTERS
  // =========================================================
  const formatCurrency = (amount) => {
    return `₹${Number(amount || 0).toLocaleString('en-IN')}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';

    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '-';

    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getCustomerName = (order) => {
    if (!order) return 'N/A';
    if (order.customer_name) return order.customer_name;

    const first = order.first_name || '';
    const last = order.last_name || '';

    const fullName = `${first} ${last}`.trim();
    return fullName || 'N/A';
  };

  const getShippingAddress = (order) => {
    if (!order) return 'N/A';
    const parts = [
      order.address,
      order.apartment,
      order.city,
      order.state,
      order.postal_code,
    ].filter(Boolean);

    return parts.join(', ') || 'N/A';
  };

  // Helper to extract active order object from response
  const activeOrder = selectedOrder?.order || selectedOrder;
  const activeItems = selectedOrder?.items || selectedOrder?.order_items || [];

  // =========================================================
  // EXPORT CSV
  // =========================================================
  const handleExportCSV = () => {
    const headers = [
      'Order Number',
      'Customer',
      'Email',
      'Phone',
      'Date',
      'Total',
      'Order Status',
      'Payment Status',
    ];

    const rows = orders.map((order) => [
      order.order_number,
      getCustomerName(order),
      order.email || '',
      order.phone || '',
      formatDate(order.created_at),
      order.total_amount,
      order.order_status,
      order.payment_status,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((field) => `"${String(field ?? '').replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', 'orders.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <MainLayout title="Order Management">
      <div className="orders-container fade-in">
        {/* HEADER CONTROLS & FILTERS */}
        <div className="orders-header-bar">
          <div className="search-input-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search by Order ID, Name, Email, Phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={() => setSearch('')}
                title="Clear Search"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="filters-row">
            {/* Status Filter Dropdown */}
            <div className="filter-dropdown-wrapper">
              <Filter size={15} className="dropdown-icon" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Status: All</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="packed">Packed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
                <option value="returned">Returned</option>
              </select>
            </div>

            {/* Payment Filter Dropdown */}
            <div className="filter-dropdown-wrapper">
              <CreditCard size={15} className="dropdown-icon" />
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
              >
                <option value="">Payment: All</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>

            {/* Sort Dropdown */}
            <div className="filter-dropdown-wrapper">
              <ArrowUpDown size={15} className="dropdown-icon" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="amount_high">Amount: High-Low</option>
                <option value="amount_low">Amount: Low-High</option>
              </select>
            </div>

            {/* Reset Button */}
            {hasActiveFilters && (
              <button
                type="button"
                className="reset-btn"
                onClick={handleResetFilters}
                title="Reset Filters"
              >
                <RotateCcw size={14} />
                <span>Reset</span>
              </button>
            )}

            {/* Export CSV Button */}
            <button
              type="button"
              className="export-btn"
              onClick={handleExportCSV}
              title="Export to CSV"
            >
              <Download size={16} />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* SUMMARY COUNT */}
        <div className="orders-count-bar">
          <span>
            Showing <strong>{orders.length}</strong> of <strong>{allOrders.length}</strong> orders
          </span>
          {hasActiveFilters && (
            <span className="filter-active-pill">
              Filtered
            </span>
          )}
        </div>

        {/* ORDERS TABLE CARD */}
        <div className="orders-card">
          <div className="table-responsive">
            <table className="orders-table">
              <thead>
                <tr>
                  <th className="th-order-id">Order ID</th>
                  <th className="th-customer">Customer</th>
                  <th className="th-date">Date</th>
                  <th className="th-total">Total Amount</th>
                  <th className="th-payment">Payment</th>
                  <th className="th-status">Status</th>
                  <th className="th-actions">Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7">
                      <div className="orders-empty-state">
                        <Clock size={24} className="spin-icon" />
                        <p>Loading orders...</p>
                      </div>
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan="7">
                      <div className="orders-empty-state">
                        <AlertCircle size={28} />
                        <h4>No orders found</h4>
                        <p>No orders matched your filter or search query.</p>
                        {hasActiveFilters && (
                          <button
                            type="button"
                            className="clear-filters-action-btn"
                            onClick={handleResetFilters}
                          >
                            <RotateCcw size={14} />
                            <span>Clear Filters</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order.id}>
                      <td className="td-order-id order-id-cell">
                        <span className="order-num-text">{order.order_number}</span>
                      </td>

                      <td className="td-customer">
                        <div className="customer-info">
                          <span className="customer-name">
                            {getCustomerName(order)}
                          </span>
                          <span className="customer-email">
                            {order.email || order.phone || '-'}
                          </span>
                        </div>
                      </td>

                      <td className="td-date order-date-cell">
                        {formatDate(order.created_at)}
                      </td>

                      <td className="td-total order-total-cell">
                        {formatCurrency(order.total_amount)}
                      </td>

                      <td className="td-payment text-center">
                        <span
                          className={`status-pill ${
                            (order.payment_status || 'pending').toLowerCase()
                          }`}
                        >
                          {order.payment_status || 'pending'}
                        </span>
                      </td>

                      <td className="td-status text-center">
                        <span
                          className={`status-pill ${
                            (order.order_status || 'pending').toLowerCase()
                          }`}
                        >
                          {getStatusIcon(order.order_status)}
                          {order.order_status || 'pending'}
                        </span>
                      </td>

                      <td className="td-actions">
                        <div className="action-btns-row">
                          <button
                            className="view-details-btn"
                            title="View Details"
                            onClick={() => handleOpenDetails(order?.id)}
                          >
                            <Eye size={14} />
                            <span>View</span>
                          </button>

                          <select
                            className="status-dropdown-select"
                            value={(order.order_status || 'pending').toLowerCase()}
                            disabled={statusUpdating}
                            onChange={(e) =>
                              handleUpdateStatus(order.id, e.target.value)
                            }
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="processing">Processing</option>
                            <option value="packed">Packed</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="returned">Returned</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ORDER DETAIL MODAL */}
      {isDetailModalOpen && activeOrder && createPortal(
        <div 
          className="modal-overlay"
          onClick={() => setIsDetailModalOpen(false)}
        >
          <div 
            className="modal-content detail-modal fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="header-title">
                <div className="modal-title-row">
                  <h2>Order Details</h2>
                  <span className="order-id-tag">
                    {activeOrder.order_number}
                  </span>
                </div>
                <div className="order-date-time">
                  Placed on: {formatDateTime(activeOrder.created_at)}
                </div>
              </div>

              <button
                className="close-modal"
                onClick={() => setIsDetailModalOpen(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="modal-badges-bar">
              <div className="badge-item">
                <span className="badge-label">Order Status:</span>
                <span className={`status-pill ${activeOrder.order_status || 'pending'}`}>
                  {getStatusIcon(activeOrder.order_status)}
                  {activeOrder.order_status || 'Pending'}
                </span>
              </div>

              <div className="badge-item">
                <span className="badge-label">Payment:</span>
                <span className={`status-pill ${activeOrder.payment_status || 'pending'}`}>
                  {activeOrder.payment_status || 'Pending'}
                </span>
              </div>

              <div className="badge-item">
                <span className="badge-label">Shipping:</span>
                <span className={`status-pill ${activeOrder.shipping_status || 'processing'}`}>
                  {activeOrder.shipping_status || 'Processing'}
                </span>
              </div>
            </div>

            <div className="order-detail-grid">
              {/* Customer Info */}
              <div className="detail-section">
                <h3>Customer Information</h3>
                <div className="info-box">
                  <div className="info-row">
                    <span className="info-key">Name:</span>
                    <span className="info-value">
                      {activeOrder.first_name || activeOrder.last_name
                        ? `${activeOrder.first_name || ''} ${activeOrder.last_name || ''}`.trim()
                        : '-'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-key">Customer ID:</span>
                    <span className="info-value">
                      {activeOrder.customer_id ? `#${activeOrder.customer_id}` : '-'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-key">Email:</span>
                    <span className="info-value">{activeOrder.email || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-key">Phone:</span>
                    <span className="info-value">{activeOrder.phone || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="detail-section">
                <h3>Shipping & Delivery Address</h3>
                <div className="info-box">
                  <div className="info-row">
                    <span className="info-key">Address:</span>
                    <span className="info-value">{activeOrder.address || '-'}</span>
                  </div>
                  {activeOrder.apartment && (
                    <div className="info-row">
                      <span className="info-key">Apartment:</span>
                      <span className="info-value">{activeOrder.apartment}</span>
                    </div>
                  )}
                  <div className="info-row">
                    <span className="info-key">City:</span>
                    <span className="info-value">{activeOrder.city || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-key">State:</span>
                    <span className="info-value">{activeOrder.state || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-key">Postal Code:</span>
                    <span className="info-value">{activeOrder.postal_code || '-'}</span>
                  </div>
                  {activeOrder.notes && (
                    <div className="info-row notes-row">
                      <span className="info-key">Notes:</span>
                      <span className="info-value">{activeOrder.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Info */}
              <div className="detail-section">
                <h3>Payment & Transaction</h3>
                <div className="info-box">
                  <div className="info-row">
                    <span className="info-key">Method:</span>
                    <span className="info-value text-capitalize">
                      {activeOrder.payment_method || activeOrder.provider || 'Razorpay'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-key">Status:</span>
                    <span className={`status-pill ${activeOrder.payment_status || 'pending'}`}>
                      {activeOrder.payment_status || 'Pending'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-key">Currency:</span>
                    <span className="info-value">{activeOrder.currency || 'INR'}</span>
                  </div>
                  {activeOrder.razorpay_order_id && (
                    <div className="info-row">
                      <span className="info-key">Razorpay Order ID:</span>
                      <span className="info-value id-code">
                        {activeOrder.razorpay_order_id}
                      </span>
                    </div>
                  )}
                  {activeOrder.razorpay_payment_id && (
                    <div className="info-row">
                      <span className="info-key">Payment ID:</span>
                      <span className="info-value id-code">
                        {activeOrder.razorpay_payment_id}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Shipping Details */}
              <div className="detail-section">
                <h3>Shipment & Tracking</h3>
                <div className="info-box">
                  <div className="info-row">
                    <span className="info-key">Shipping Status:</span>
                    <span className={`status-pill ${activeOrder.shipping_status || 'processing'}`}>
                      {activeOrder.shipping_status || 'Processing'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-key">Courier:</span>
                    <span className="info-value">
                      {activeOrder.courier_name || 'Not Assigned'}
                    </span>
                  </div>
                  {activeOrder.awb_code && (
                    <div className="info-row">
                      <span className="info-key">AWB Tracking Code:</span>
                      <span className="info-value id-code">
                        {activeOrder.awb_code}
                      </span>
                    </div>
                  )}
                  {activeOrder.shipment_id && (
                    <div className="info-row">
                      <span className="info-key">Shipment ID:</span>
                      <span className="info-value id-code">
                        {activeOrder.shipment_id}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Coupon Info */}
              {(activeOrder.coupon_code || Number(activeOrder.discount_amount) > 0) && (
                <div className="detail-section full-width">
                  <h3>Coupon & Discount Applied</h3>
                  <div className="info-box coupon-box">
                    <div className="info-row">
                      <span className="info-key">Coupon Code:</span>
                      <span className="info-value coupon-tag">
                        {activeOrder.coupon_code || 'APPLIED'}
                      </span>
                    </div>
                    {activeOrder.coupon_type && (
                      <div className="info-row">
                        <span className="info-key">Discount Type:</span>
                        <span className="info-value text-capitalize">
                          {activeOrder.coupon_type}
                        </span>
                      </div>
                    )}
                    <div className="info-row">
                      <span className="info-key">Total Discount:</span>
                      <span className="info-value discount-text">
                        - {formatCurrency(activeOrder.discount_amount)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div className="detail-section full-width">
                <h3>Ordered Items ({activeItems.length})</h3>
                <div className="items-table-wrapper">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Item Details</th>
                        <th>Price</th>
                        <th>Qty</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeItems.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="empty-items-cell">
                            No items found for this order.
                          </td>
                        </tr>
                      ) : (
                        activeItems.map((item, idx) => (
                          <tr key={item.id || idx}>
                            <td className="item-name-cell">
                              <div className="item-details">
                                <span className="item-title">
                                  {item.product_name || item.name || 'Product'}
                                </span>
                                {(item.variant_name || item.sku) && (
                                  <span className="item-variant">
                                    {item.variant_name} {item.sku && `(SKU: ${item.sku})`}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>{formatCurrency(item.price || item.unit_price)}</td>
                            <td>{item.quantity || 1}</td>
                            <td className="item-subtotal">
                              {formatCurrency(
                                Number(item.price || item.unit_price || 0) *
                                  Number(item.quantity || 1)
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="text-right">
                          Subtotal
                        </td>
                        <td>
                          {formatCurrency(
                            activeOrder.subtotal ||
                              activeItems.reduce(
                                (sum, i) =>
                                  sum + Number(i.price || 0) * Number(i.quantity || 1),
                                0
                              )
                          )}
                        </td>
                      </tr>

                      {Number(activeOrder.discount_amount) > 0 && (
                        <tr className="discount-row">
                          <td colSpan="3" className="text-right">
                            Discount {activeOrder.coupon_code ? `(${activeOrder.coupon_code})` : ''}
                          </td>
                          <td className="discount-text">
                            - {formatCurrency(activeOrder.discount_amount)}
                          </td>
                        </tr>
                      )}

                      <tr>
                        <td colSpan="3" className="text-right">
                          Shipping Charges
                        </td>
                        <td>
                          {Number(activeOrder.shipping_charge) > 0
                            ? formatCurrency(activeOrder.shipping_charge)
                            : 'Free (₹0.00)'}
                        </td>
                      </tr>

                      <tr className="grand-total">
                        <td colSpan="3" className="text-right">
                          Grand Total
                        </td>
                        <td>
                          {formatCurrency(activeOrder.total_amount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="save-btn"
                onClick={() => setIsDetailModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </MainLayout>
  );
};

export default Orders;